import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "@mui/material";
import {DeleteOutline, KeyboardArrowDown, KeyboardArrowUp} from "@mui/icons-material";
import dateCardTemplateService from "../../../services/dateCardTemplateService";
import {IDateCardTemplate} from "../../../types/DateCardTemplate";

interface Props {
    open: boolean;
    categories: string[];
    templates: IDateCardTemplate[];
    onClose: () => void;
    onSaved: (newOrder: string[]) => void;
}

export const ReorderCategoriesModal: React.FC<Props> = ({open, categories, templates, onClose, onSaved}) => {
    const [order, setOrder] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setOrder([...categories]);
        setError(null);
    }, [categories, open]);

    function move(index: number, direction: -1 | 1) {
        const next = [...order];
        const swapIndex = index + direction;
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
        setOrder(next);
    }

    function deleteCategory(cat: string) {
        const hasTemplates = templates.some((t) => t.category === cat);
        if (hasTemplates) {
            setError(`Delete all designs in "${cat}" before removing this category.`);
            return;
        }
        setError(null);
        setOrder((prev) => prev.filter((c) => c !== cat));
    }

    async function save() {
        await dateCardTemplateService.updateCategoryOrder(order);
        onSaved(order);
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
            <DialogTitle>Reorder Categories</DialogTitle>
            <DialogContent>
                <List disablePadding>
                    {order.map((cat, i) => (
                        <ListItem
                            key={cat}
                            disablePadding
                            sx={{display: 'flex', alignItems: 'center', py: 0.5}}
                        >
                            <Box sx={{display: 'flex', flexDirection: 'column', mr: 1}}>
                                <IconButton
                                    size={'small'}
                                    disabled={i === 0}
                                    onClick={() => move(i, -1)}
                                >
                                    <KeyboardArrowUp fontSize={'small'}/>
                                </IconButton>
                                <IconButton
                                    size={'small'}
                                    disabled={i === order.length - 1}
                                    onClick={() => move(i, 1)}
                                >
                                    <KeyboardArrowDown fontSize={'small'}/>
                                </IconButton>
                            </Box>
                            <ListItemText primary={cat} sx={{flexGrow: 1}}/>
                            <IconButton
                                size={'small'}
                                color={'error'}
                                onClick={() => deleteCategory(cat)}
                            >
                                <DeleteOutline fontSize={'small'}/>
                            </IconButton>
                        </ListItem>
                    ))}
                </List>
                {error && (
                    <Typography variant={'body2'} color={'error'} sx={{mt: 1}}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant={'contained'} onClick={() => void save()}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};
